import React, { useEffect, useState } from 'react';
import { createLogger } from '../../../shared/config/logger';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { customersSliceAPI } from '../store/customersSlice';
import { useAuth } from '../../auth/hooks/useAuth';
import { useMerchantAuth } from '../../../contexts/AuthContext';
import { sortCustomers, filterCustomers } from '../utils/dataTransformers';
import { calculateCustomerMetrics } from '../utils/metrics';
import { usePagination } from '../hooks/usePagination';
import { CustomerMetrics } from '../components/CustomerMetrics';
import { CustomerTable } from '../components/CustomerTable';
import { CustomerPagination } from '../components/CustomerPagination';
import { CustomerDeleteModal } from '../components/CustomerDeleteModal';
const logger = createLogger('Customers.jsx');

export default function Customers() {
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedPassId, setSelectedPassId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const dispatch = useAppDispatch();
    const list = useAppSelector((state) => state.customers.list);
    const {
        state: { userId },
    } = useAuth();
    const { hasPermission } = useMerchantAuth();

    useEffect(() => {
        if (userId) {
            dispatch(customersSliceAPI.fetchAllCustomers());
        }
    }, [userId, dispatch]);

    // Filter and sort customers using extracted utilities
    const filteredPasses = React.useMemo(
        () => filterCustomers(list, searchTerm),
        [list, searchTerm]
    );

    const sortedPasses = React.useMemo(
        () => sortCustomers(filteredPasses, sortConfig),
        [filteredPasses, sortConfig]
    );

    const paginationResetKey = `${searchTerm}|${sortConfig.key}|${sortConfig.direction}`;

    // Use pagination hook
    const { currentPage, totalPages, paginatedItems: paginatedPasses, setCurrentPage } = usePagination(
        sortedPasses,
        7,
        paginationResetKey
    );

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleDeleteClick = (passId) => {
        setSelectedPassId(passId);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPassId) {
            return;
        }

        try {
            await dispatch(customersSliceAPI.deleteCustomer(selectedPassId)).unwrap();
            logger.info('Successfully deleted customer', { passId: selectedPassId });
            setShowModal(false);
            setSelectedPassId(null);
        } catch (err) {
            logger.error('Failed to delete customer', { error: err.message, passId: selectedPassId });
            alert('Failed to delete customer');
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedPassId(null);
    };

    // Find the customer object for the modal
    const selectedCustomer = selectedPassId 
        ? list.find(c => c.wallet_pass_id === selectedPassId)
        : null;

    // Calculate metrics using extracted utility
    const { totalCustomers: totalCustomer, newCustomersThisMonth, activeCustomers, inactiveCustomers } =
        React.useMemo(() => calculateCustomerMetrics(list), [list]);

    return (
        <div className="p-4 sm:p-6 w-full max-w-7xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">Customers</h2>

            <CustomerMetrics
                totalCustomers={totalCustomer}
                newCustomersThisMonth={newCustomersThisMonth}
                activeCustomers={activeCustomers}
                inactiveCustomers={inactiveCustomers}
            />

            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by name, email, or phone"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded"
                />
            </div>

            <CustomerTable
                customers={paginatedPasses}
                sortConfig={sortConfig}
                onSort={handleSort}
                onDelete={handleDeleteClick}
                canDelete={hasPermission('delete_pass')}
            />

            <CustomerPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            <CustomerDeleteModal
                isOpen={showModal}
                customer={selectedCustomer}
                onClose={handleModalClose}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
}
